/* eslint-disable react/no-unescaped-entities */
import { Euro, Wallet, Home, BookOpen, ShieldCheck, AlertTriangle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import GermanyResourceShell from './_components/GermanyResourceShell';

const DESC = 'Cost of studying in Germany 2026 for Indian students: tuition fees, €11,904 blocked account, monthly living expenses by city, insurance, and total annual budget.';

const TUITION_TABLE = [
  { type: 'Public Universities (Bachelor / Master)',  tuition: '€0 — €300 per semester',           note: 'Most states (Berlin, Bavaria, NRW) are tuition-free. Only Baden-Württemberg charges €1,500/sem.' },
  { type: 'Private Universities (Bachelor / Master)', tuition: '€10,000 — €25,000 per year',       note: 'IU, GISMA, Jacobs, EBS — English-taught, faster admissions.' },
  { type: 'Public — Non-EU State Tuition Fees',       tuition: '€1,500 per semester (BW only)',    note: 'Applies only to non-EU students in Baden-Württemberg.' },
  { type: 'Semester Contribution (all universities)', tuition: '€150 — €350 per semester',         note: 'Mandatory — includes public-transport pass (Semesterticket).' },
];

const LIVING_COSTS = [
  { city: 'Munich',     rent: '€600 – €900',  food: '€220',  transport: '€38 (Semesterticket)', personal: '€150', total: '€1,008 – €1,308', cluster: 'Tier 1 (Premium)' },
  { city: 'Frankfurt',  rent: '€500 – €800',  food: '€200',  transport: '€90 (full)',             personal: '€130', total: '€920 – €1,220',   cluster: 'Tier 1 (Premium)' },
  { city: 'Hamburg',    rent: '€450 – €700',  food: '€200',  transport: '€110 (full)',            personal: '€130', total: '€890 – €1,140',   cluster: 'Tier 2 (Major)' },
  { city: 'Berlin',     rent: '€450 – €750',  food: '€190',  transport: '€29 (Sozialticket)',     personal: '€140', total: '€809 – €1,109',   cluster: 'Tier 2 (Major)' },
  { city: 'Stuttgart',  rent: '€400 – €650',  food: '€190',  transport: '€89 (full)',             personal: '€130', total: '€809 – €1,059',   cluster: 'Tier 2 (Major)' },
  { city: 'Leipzig',    rent: '€280 – €450',  food: '€180',  transport: '€0 (Semesterticket inc)',personal: '€110', total: '€570 – €740',     cluster: 'Tier 3 (Budget)' },
  { city: 'Dresden',    rent: '€300 – €480',  food: '€180',  transport: '€0 (Semesterticket inc)',personal: '€120', total: '€600 – €780',     cluster: 'Tier 3 (Budget)' },
];

const ONE_TIME = [
  { item: 'Visa application fee',            cost: '€75 (₹6,800)',          when: 'At visa appointment' },
  { item: 'APS Certificate (Indians)',       cost: '₹18,500',               when: 'Before university application' },
  { item: 'Flight (Delhi/Mumbai → Frankfurt)', cost: '₹35,000 – ₹65,000',   when: '60+ days before travel' },
  { item: 'Initial setup (Anmeldung, deposit)', cost: '€800 – €1,500',     when: 'First 2 weeks in Germany' },
  { item: 'Translation + apostille fees',     cost: '₹8,000 – ₹15,000',     when: 'Before visa filing' },
];

const FAQS = [
  { q: 'How much does it cost to study in Germany for Indian students in 2026?',
    a: 'Public university Bachelors/Masters cost ~€0 in tuition + €11,904 living expenses/year (blocked account requirement). Total first-year cost from India: roughly ₹12–15 lakh (tuition + visa + flight + first-year living). Private universities add €10,000–€25,000/year in tuition on top. Many Indian students get DAAD or scholarship funding, reducing this further.' },
  { q: 'Is Germany cheap or expensive for Indian students?',
    a: 'Germany is the cheapest major Western study destination because most public universities charge zero tuition. Compared to the UK (~₹35–40 lakh/year) or USA (~₹45–60 lakh/year), Germany at ₹12–15 lakh/year (largely on living costs) is 3–4x cheaper. Cities matter: Leipzig and Dresden are ~50% cheaper than Munich.' },
  { q: 'What is the cheapest city to study in Germany?',
    a: 'Leipzig (€570–€740/month total) is the cheapest mid-sized German city for students. Other budget-friendly options include Dresden, Bremen, Halle, and Magdeburg. Munich, Frankfurt and Hamburg are the most expensive — choose East German cities or Tier-3 university towns for the best value.' },
  { q: 'Can I work part-time to cover living costs?',
    a: 'Yes. Indian students on a German student visa can work 120 full days OR 240 half-days per year (~20 hrs/week during semesters). Typical hourly wage is €12–€15. Working part-time can cover 30–50% of monthly living costs but is NOT enough to replace the blocked account requirement at visa stage.' },
  { q: 'Are there hidden costs I should budget for?',
    a: 'Yes — three commonly missed costs: (1) Health insurance — €125–€130/month for under-30 students (mandatory); (2) GEZ broadcasting fee — €18.36/month (mandatory for every household); (3) Semester contribution — €150–€350/semester, includes a transport pass. Budget an extra €200–€250/month beyond rent + food.' },
  { q: 'Does my €11,904 blocked account cover all annual living costs?',
    a: 'It covers minimum living costs (€992/month) — sufficient for Tier-3 cities like Leipzig but tight in Munich or Frankfurt. Most students supplement with part-time work (€300–€500/month) starting from semester 2 onwards. Budget an additional €100–€200/month for incidentals.' },
];

const GermanyCostOfStudyPage = () => (
  <GermanyResourceShell
    slug="cost-of-study"
    breadcrumbLabel="Cost of Study"
    articleHeadline="Cost of Studying in Germany 2026 — Complete Budget for Indian Students"
    articleDescription={DESC}
    faqs={FAQS}
    related={['blocked-account', 'scholarships', 'student-accommodation']}
  >
    {/* Hero */}
    <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}>
      <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-5">
            <Euro className="w-3.5 h-3.5 text-[#C5A028]" />
            <span className="text-xs text-white/90 font-medium">Germany Resource — Cost of Study</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-syne text-white leading-tight mb-4">
            Cost of Studying in Germany 2026 — Full Budget for Indian Students
          </h1>
          <p className="text-base lg:text-lg text-slate-300 leading-relaxed mb-6">
            Germany remains the most affordable major Western study destination — tuition-free public universities + structured cost-of-living rules make it 3–4× cheaper than the UK or USA. This is the complete 2026 cost breakdown — tuition, blocked account, monthly living expenses by city, insurance, semester contribution, and one-time setup costs.
          </p>
          <div className="grid grid-cols-3 gap-3 max-w-2xl">
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Public Tuition</div>
              <div className="text-base font-bold text-[#C5A028]">€0 / sem</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Living Cost</div>
              <div className="text-base font-bold text-[#C5A028]">€992 / mo</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">1st-Year Total</div>
              <div className="text-base font-bold text-[#C5A028]">~₹13L</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Tuition */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-5">Tuition Fees in Germany (2026)</h2>
        <p className="text-[15px] leading-7 text-slate-700 mb-6">
          Germany's public universities are world-famous for charging zero tuition — including for international students. The only exception is Baden-Württemberg, which introduced a €1,500/semester fee for non-EU students in 2017. All public universities, including TU9 (Germany's top 9 technical universities), charge only a modest "semester contribution" of €150–€350.
        </p>
        <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-[#003087] text-white">
              <tr>
                <th className="px-4 py-3 text-left font-syne">University Type</th>
                <th className="px-4 py-3 text-left font-syne">Tuition</th>
                <th className="px-4 py-3 text-left font-syne">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {TUITION_TABLE.map((row) => (
                <tr key={row.type}>
                  <td className="px-4 py-3 font-semibold text-[#003087]">{row.type}</td>
                  <td className="px-4 py-3 font-medium">{row.tuition}</td>
                  <td className="px-4 py-3 text-slate-600">{row.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    {/* Living Costs */}
    <section className="py-14 lg:py-18 bg-slate-50">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-2">Monthly Living Costs by City (2026)</h2>
        <p className="text-sm text-slate-600 mb-6">All amounts in EUR per month. East-German cities (Leipzig, Dresden) are 30–50% cheaper than Munich or Frankfurt.</p>
        <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-[#003087] text-white">
              <tr>
                <th className="px-4 py-3 text-left font-syne">City</th>
                <th className="px-4 py-3 text-left font-syne">Rent</th>
                <th className="px-4 py-3 text-left font-syne">Food</th>
                <th className="px-4 py-3 text-left font-syne">Transport</th>
                <th className="px-4 py-3 text-left font-syne">Personal</th>
                <th className="px-4 py-3 text-left font-syne">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {LIVING_COSTS.map((c) => (
                <tr key={c.city}>
                  <td className="px-4 py-3 font-bold text-[#003087]">
                    {c.city}
                    <div className="text-[10px] text-slate-500 font-normal mt-0.5">{c.cluster}</div>
                  </td>
                  <td className="px-4 py-3">{c.rent}</td>
                  <td className="px-4 py-3">{c.food}</td>
                  <td className="px-4 py-3">{c.transport}</td>
                  <td className="px-4 py-3">{c.personal}</td>
                  <td className="px-4 py-3 font-bold text-emerald-700">{c.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    {/* One-time costs */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-5">One-Time Setup Costs (From India)</h2>
        <div className="overflow-x-auto bg-slate-50 border border-slate-200 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-[#003087] text-white">
              <tr>
                <th className="px-4 py-3 text-left font-syne">Item</th>
                <th className="px-4 py-3 text-left font-syne">Cost</th>
                <th className="px-4 py-3 text-left font-syne">When</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {ONE_TIME.map((o) => (
                <tr key={o.item}>
                  <td className="px-4 py-3 font-semibold">{o.item}</td>
                  <td className="px-4 py-3">{o.cost}</td>
                  <td className="px-4 py-3 text-slate-600">{o.when}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-blue-50 border-l-4 border-[#003087] p-4 mt-6 rounded-r-lg">
          <div className="text-xs font-bold text-[#003087] uppercase tracking-wide mb-1">Total 1st-Year Budget (From India)</div>
          <p className="text-sm text-slate-700">
            <strong>Public university:</strong> €11,904 (blocked account) + ~€1,200 (visa + flight + setup) + ₹30,000 (APS + translations) = <strong>~₹12–13 lakh</strong> all-in.<br />
            <strong>Private university:</strong> Add tuition of €10,000–€25,000/year on top = <strong>~₹22–35 lakh</strong> all-in.
          </p>
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="py-14 lg:py-18 bg-slate-50">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-7">Frequently Asked Questions</h2>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <details key={i} className="group bg-white border border-slate-200 rounded-xl p-5">
              <summary className="font-syne font-semibold text-slate-900 cursor-pointer text-[15px] flex items-start justify-between">
                <span className="pr-6">{f.q}</span>
                <span className="text-[#003087] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm leading-7 text-slate-700">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  </GermanyResourceShell>
);

export default GermanyCostOfStudyPage;
